/**
 * Bot Component Constants
 * Centralized constants used across Bot components
 */

// ========== Form Configuration ==========
export const BOT_PAGE_CONFIG = {
  name: 'bot',
  title: 'KAI Select',
} as const;

export const BOT_INFO_BRANDING = {
  brand: 'KAI',
  product: 'Select',
  greeting:
    "Hey, I'm KAI-Select, the Kinetix AI Air Handling Unit Selector. Do you want me to design your Air Handling Unit for you?",
  instruction:
    'Just tell me about the air handling unit you want to design by completing the form below, and click on SELECT.',
} as const;

// ========== Section Configuration ==========
export const MAX_SECTIONS_PER_TUNNEL = 20;

export const SECTION_TYPES = {
  PLENUM: 'plenum',
  FAN: 'fan',
  COIL: 'coil',
  FILTER: 'filter',
  ER_WHEEL: 'er_wheel',
  EMPTY: '',
} as const;

// ========== Accessory Configuration ==========
export const ACCESSORY_TYPES = {
  DRAIN_PAN: 'drain_pan',
  ACCESS_DOOR: 'access_door',
  OPENING: 'opening',
  DAMPER: 'damper',
} as const;

export const ACCESSORY_DEFAULT_VALUES = {
  drain_pan: {
    conn_hand_id: 2,
    mountLocation: 'left',
  },
  opening: {
    mounting_plane_id: 3,
    mountLocation: 'left',
  },
  access_door: {
    mounting_plane_id: 6,
    mountLocation: 'left',
  },
} as const;

// ========== Voltage Configuration ==========
export const VOLTAGE_FILTER_OPTIONS = ['208/3/60', '230/3/60', '460/3/60'] as const;
export type VoltageOption = (typeof VOLTAGE_FILTER_OPTIONS)[number];

// ========== Position & Layout Constants ==========
export const ACCESSORIES_PANEL_OFFSETS = {
  SECTION_OFFSET: 55,
  BASE_TOP_OFFSET: 235,
  BUFFER_OFFSET: 50,
} as const;

// ========== Storage Keys ==========
export const STORAGE_KEYS = {
  BOT_FORM: 'bot-form',
} as const;

// ========== Status & Message Text ==========
export const BOT_MESSAGES = {
  DESIGN_IN_PROGRESS: 'Air Handling Unit selection is in progress.',
  DESIGN_WAIT: 'Please wait. This process may take a few minutes.',
  BUTTON_SELECT: 'SELECT',
  BUTTON_CANCEL: 'CANCEL',
  BUTTON_RESET: 'reset',
  TUNNEL_LABEL: 'Tunnel',
  SECTIONS_LABEL: 'Sections',
  ACCESSORIES_TITLE: 'Section Accessories',
  SUMMARY_TITLE: 'Summary',
} as const;

// ========== Form Field Placeholders ==========
export const FORM_PLACEHOLDERS = {
  AHU_TAG: 'Enter AHU tag',
  AIRFLOW: '__',
  AIRFLOW_ALTERNATE: '- -',
} as const;

// ========== Validation Constants ==========
export const VALIDATION_RULES = {
  MIN_AIRFLOW: 0,
  MAX_AIRFLOW: 100000,
  AHU_TAG_MIN_LENGTH: 1,
  AHU_TAG_MAX_LENGTH: 100,
} as const;

// ========== Accessory Removal Rules ==========
/**
 * Rules for when accessories cannot be removed
 */
export const ACCESSORY_REMOVAL_RESTRICTIONS = {
  /**
   * drain_pan cannot be removed from coil sections with type_id === 1
   */
  DRAIN_PAN_COIL_RESTRICTION: {
    accessoryType: 'drain_pan',
    sectionType: 'coil',
    condition: (section: any) => section.type_id === 1,
  },

  /**
   * opening cannot be removed from the last section in a tunnel
   */
  OPENING_LAST_SECTION_RESTRICTION: {
    accessoryType: 'opening',
    condition: (isLastSection: boolean) => isLastSection,
  },

  /**
   * access_door cannot be removed from fan, plenum, or coil (duty_id=1) sections
   */
  ACCESS_DOOR_RESTRICTION: {
    accessoryType: 'access_door',
    allowedSectionTypes: ['fan', 'plenum'],
    coilCondition: (section: any) => section.duty_id === 1,
  },
} as const;

// ========== Section Protection Rules ==========
/**
 * Rules for when sections cannot be removed
 */
export const SECTION_REMOVAL_RESTRICTIONS = {
  /**
   * er_wheel cannot be removed from tunnel[1] if it exists in tunnel[0]
   */
  ER_WHEEL_TUNNEL_SYNC: 'Cannot remove er_wheel from second tunnel if it exists in first',

  /**
   * fan section removal depends on configuration rules
   */
  FAN_CONFIG_DEPENDENT: 'Cannot remove fan section due to configuration constraints',
} as const;

// ========== CSS Classes ==========
export const CSS_CLASSES = {
  BOT_INFO: 'bot-info',
  BOT_FORM: 'bot-form',
  BOT_AHU: 'bot-ahu',
  BOT_TUNNEL: 'bot-tunnel',
  BOT_ACCESSORIES: 'bot-accessories',
  BOT_SUMMARY: 'bot-summary',
  ACTIVE: 'is-active',
  SELECTED: 'is-selected',
  ERROR: 'has_error',
  OPEN: 'is-open',
  CLOSED: 'is-closed',
} as const;

// ========== Accessibility & ARIA Labels ==========
export const ARIA_LABELS = {
  SECTION_DRAG_HANDLE: 'Reorder section',
  ACCESSORIES_BUTTON: 'Toggle accessories',
  REMOVE_SECTION: 'Remove section',
  ADD_SECTION: 'Add section',
  FORM_TAB_LIST: 'Bot modes',
} as const;

// ========== Tooltip Text ==========
export const TOOLTIP_TEXT = {
  ADD_SECTION: 'Add Section',
  DELETE_SECTION: 'Delete Section',
  RESET_FORM: 'Reset all values to defaults',
} as const;

// ========== Drag & Drop Configuration ==========
export const DRAG_DROP_CONFIG = {
  AXIS: 'vertical' as const,
  DIRECTION: 'vertical' as const,
  SCROLL_SPEED: 5,
} as const;

// ========== Loader Configuration ==========
export const LOADER_CONFIG = {
  CLASSNAME: 'bg-custom-grey',
  SIZE: 'large' as const,
} as const;

// ========== Error Messages ==========
export const ERROR_MESSAGES = {
  INVALID_SELECTION: 'Invalid section selection',
  INVALID_TUNNEL_INDEX: 'Invalid tunnel index',
  INVALID_SECTION_INDEX: 'Invalid section index',
  SECTION_NOT_FOUND: 'Section not found',
  ACCESSORY_NOT_FOUND: 'Accessory not found',
  OPERATION_NOT_ALLOWED: 'This operation is not allowed',
} as const;

// ========== Success Messages ==========
export const SUCCESS_MESSAGES = {
  AHU_DESIGNED: 'Air Handling Unit design completed successfully!',
  SECTION_ADDED: 'Section added successfully',
  ACCESSORY_ADDED: 'Accessory added successfully',
  FORM_RESET: 'Form reset to defaults',
} as const;

// ========== Ref Names (for testing/debugging) ==========
export const REF_NAMES = {
  FORM_BODY: 'formBodyRef',
  ACCESSORIES_PANEL: 'accessoriesPanelRef',
  PROJECT_SELECT: 'projectIdSelectRef',
} as const;

// ========== Feature Flags (if needed) ==========
export const FEATURE_FLAGS = {
  ENABLE_DRAG_DROP: true,
  ENABLE_ACCESSORY_PANEL: true,
  ENABLE_STORAGE_SYNC: true,
  ENABLE_FORM_VALIDATION: true,
  ENABLE_ERROR_BOUNDARY: true,
} as const;

